import DatePickerInput from '@/components/inputs/DatePickerInput';
import { AudienceSyncTimeBasedSchedule } from '@/models/audiences';
import { upperCaseFirstChar } from '@/utils';
import { differenceInCalendarDays, format, formatDistanceToNow, formatRelative, parse, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type AudienceSyncRepeatStartProps = {
  scheduleConfiguration: AudienceSyncTimeBasedSchedule;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
};

export const AudienceSyncRepeatStart: FC<AudienceSyncRepeatStartProps> = ({ scheduleConfiguration, setFieldValue }) => {
  const { t } = useTranslation('ALL');

  const initialStartsOn = scheduleConfiguration.startsOn ? parseISO(scheduleConfiguration.startsOn) : new Date();
  initialStartsOn.setSeconds(0);
  initialStartsOn.setMilliseconds(0);

  const [startDay, setStartDay] = useState<Date>(initialStartsOn);
  const [startHour, setStartHour] = useState<string>(format(initialStartsOn, 'HH:mm'));
  const [readableStartDate, setReadableStartDate] = useState<string>();

  useEffect(() => {
    const parsed = parse(startHour, 'HH:mm', startDay);
    setFieldValue('scheduleConfiguration.startsOn', `${formatInTimeZone(parsed, 'UTC', 'yyyy-MM-dd')}T${formatInTimeZone(parsed, 'UTC', 'HH:mm')}:00.000Z`);

    const daysDiff = differenceInCalendarDays(parsed, new Date());
    if (daysDiff < -6) {
      setReadableStartDate(`(${formatDistanceToNow(parsed)} ${t('ago')})`);
    } else if (daysDiff === 0 || daysDiff === 1) {
      setReadableStartDate(`(${upperCaseFirstChar(formatRelative(parsed, new Date()))})`);
    } else if (daysDiff < 7) {
      setReadableStartDate(`(${t('this')} ${formatRelative(parsed, new Date())})`);
    } else if (daysDiff < 14) {
      setReadableStartDate(`(${format(parsed, `iiii '${t('nextWeek')}' KK:mm`)})`);
    } else {
      setReadableStartDate(`(${t('in')} ${formatDistanceToNow(parsed)})`);
    }
  }, [startDay, startHour]);

  return (
    <div className='mb-6 flex flex-row items-center'>
      <span className='mr-4'>{t('itStartsOn')}</span>

      <div className='mr-4 w-32'>
        <DatePickerInput
          dateFormat='dd/MM/yyyy'
          className={'shadow-sm'}
          selected={startDay}
          onChange={(e) => e !== null && !Array.isArray(e) && setStartDay(e)}
        />
      </div>
      <div className='mr-4 w-24'>
        <input
          type='time'
          className='rounded-md border border-gray-300 px-2 shadow-sm placeholder:text-gray-400 focus:border focus:border-blue-600 focus:outline-none sm:text-sm'
          value={startHour}
          onChange={(e) => setStartHour(e.target.value)}
        />
      </div>
      {readableStartDate && <span>{readableStartDate}</span>}
    </div>
  );
};
